/**
 * One parcel-shaped view of any SmartSend item.
 *
 * `item.Consignment` is only populated when the user asked for several parcels, so every display and
 * validation surface otherwise has to know which of two shapes it is looking at. Most of them do not,
 * and read `MarketplaceOrderLines` / `PackageDimensions` unconditionally - which is wrong for a
 * multi-parcel item and disagrees with what the API actually books.
 *
 * These accessors present the consignment shape whether or not one is stored, synthesising a single
 * parcel from `PackageDimensions` + `MarketplaceOrderLines` when it is not.
 *
 * READ-ONLY BY DESIGN. Nothing here persists a consignment, and there are deliberately no setters:
 * writing a declaration still has to branch on whether a consignment exists, and hiding that behind
 * an accessor would make single-parcel declaration edits look safe when they still overwrite the
 * imported marketplace record. Items saved without a consignment keep arriving from the server, so
 * this synthesis is permanent infrastructure rather than migration scaffolding.
 *
 * PRECEDENCE MIRRORS THE API. `OrderMapper.MapParcels` in edge-smartsend-api is the authority on what
 * actually gets booked, including the per-axis dimensions fallback
 * (`consignmentParcel.Dimensions?.X ?? i.PackageDimensions.X`, so a parcel carrying only a Weight
 * inherits L/W/H). Duplicating those rules approximately is how the current disagreements arose, so
 * they are mirrored here exactly - with one deliberate exception, see `getParcels`.
 */
import {
  ConsignmentParcelApiModel,
  ConsignmentParcelItemApiModel,
  Dimensions,
  MarketplaceOrderApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { SumNumber } from '../helpers/ArrayHelper';
import { MapMarketplaceOrderlineToConsignmentParcel } from '../../components/smartsend/grouping/MapMarketplaceOrderlineToConsignmentParcel';
import { round2 } from './allocate';

/** True when the item carries a stored parcel configuration, i.e. everything below is not synthesised. */
export const hasStoredParcels = (item: SmartSendItemApiModel): boolean => item?.Consignment?.Parcels?.length > 0;

/**
 * Deliberately not `linesToContents` from allocate.ts: that mints a fresh UID on every call, which is
 * correct when seeding an editable plan and wrong for a read accessor - unstable identity would break
 * React keys and defeat memoisation. Synthesised rows borrow the line's own Id as their UID, so
 * reading twice gives the same answer.
 */
const lineToContent = (line: MarketplaceOrderApiModel): ConsignmentParcelItemApiModel => ({
  ...MapMarketplaceOrderlineToConsignmentParcel(line),
  Id: line.Id,
  UID: line.Id,
  Value: round2(line.Value)
});

/** Per axis, matching MapParcels - nullish only, so an explicit 0 is kept rather than replaced. */
const resolveDimensions = (parcel: Dimensions, fallback: Dimensions): Dimensions => ({
  Weight: parcel?.Weight ?? fallback?.Weight,
  Length: parcel?.Length ?? fallback?.Length,
  Width: parcel?.Width ?? fallback?.Width,
  Height: parcel?.Height ?? fallback?.Height
});

/**
 * The item's parcels, with dimensions resolved against `PackageDimensions` exactly as booking does.
 * An item with no stored configuration yields one parcel holding every order line.
 *
 * THE ONE DELIBERATE DIVERGENCE FROM `MapParcels`: booking silently DROPS any parcel with no
 * contents, and if every parcel is empty falls through to the single-parcel branch and books an
 * entirely different shipment. That behaviour is a bug to prevent, not to reproduce - hiding an empty
 * parcel here would hide the very thing the user needs to fix. Empty parcels are returned, and
 * `PreQuoteValidation` / `contentIsIncomplete` block on them instead.
 */
export function getParcels(item: SmartSendItemApiModel): ConsignmentParcelApiModel[] {
  if (hasStoredParcels(item)) {
    return item.Consignment.Parcels.map((parcel) => ({
      ...parcel,
      Dimensions: resolveDimensions(parcel.Dimensions, item.PackageDimensions)
    }));
  }

  return [
    {
      Contents: (item?.MarketplaceOrderLines ?? []).map(lineToContent),
      Dimensions: resolveDimensions(null, item?.PackageDimensions),
      Id: item?.Id,
      UID: item?.Id,
      CanAdd: false
    }
  ];
}

export const getParcelCount = (item: SmartSendItemApiModel): number =>
  hasStoredParcels(item) ? item.Consignment.Parcels.length : 1;

/**
 * Every declared row across every parcel - the customs declaration as the courier will receive it.
 * Note this is not a per-line view: the same order line can contribute rows to several parcels.
 */
export const getDeclaredContents = (item: SmartSendItemApiModel): ConsignmentParcelItemApiModel[] =>
  // `?? []` matters: flatMap over a parcel whose Contents is undefined yields [undefined], not [], and
  // every caller then dereferences a row that isn't there.
  getParcels(item).flatMap((p) => p.Contents ?? []);

/**
 * The declared value. Mirrors the API, which sums contents whenever a consignment exists and only
 * falls back to `item.Value` when there is none.
 *
 * Because of the pad-with-copies overflow contract this can legitimately exceed the ordered value's
 * quantity basis, so do not treat a mismatch against the order as an error.
 */
export function getDeclaredValue(item: SmartSendItemApiModel): number {
  if (hasStoredParcels(item)) {
    return round2(SumNumber(getDeclaredContents(item).map((c) => c.Value)));
  }

  return round2(item?.Value > 0 ? item.Value : SumNumber((item?.MarketplaceOrderLines ?? []).map((l) => l.Value)));
}

/** Total declared units. May exceed the ordered quantity - see `getDeclaredValue`. */
export const getDeclaredQuantity = (item: SmartSendItemApiModel): number =>
  SumNumber(getDeclaredContents(item).map((c) => c.Quantity || 1));

/**
 * Dimensions to SHOW for an item, as opposed to the dimensions of any one parcel.
 *
 * Weight is the total across parcels, because that is what the customer is charged on. This is the
 * same number `GetItemWeight` produces - pinned by a test rather than by importing it, since
 * SmartSendSummaryGenerator pulls in caches, context and the filter applier, and this module is
 * deliberately dependency-light.
 *
 * Length/Width/Height are returned ONLY for a single parcel. A three-parcel consignment has three
 * sets of dimensions and no meaningful total, so showing one parcel's box as though it were the
 * shipment's is worse than showing nothing - display sites should show the parcel count instead and
 * send the user to the parcel editor for the detail.
 */
export function getDisplayDimensions(item: SmartSendItemApiModel): Dimensions {
  const parcels = getParcels(item);
  const weight = round2(SumNumber(parcels.map((p) => p.Dimensions?.Weight)));

  if (parcels.length !== 1) {
    return { Weight: weight };
  }

  const { Length, Width, Height } = parcels[0].Dimensions;
  return { Weight: weight, Length, Width, Height };
}
